import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiZap, FiHeart, FiCpu, FiCompass, FiStar, FiSearch } = FiIcons;

const categories = [
  { id: 'all', label: 'All', icon: FiCompass },
  { id: 'motivation', label: 'Motivation', icon: FiZap },
  { id: 'wisdom', label: 'Wisdom', icon: FiStar },
  { id: 'love', label: 'Love', icon: FiHeart },
  { id: 'tech', label: 'Tech', icon: FiCpu },
];

const quotes = [
  { text: "The path to success is to take massive, determined action.", author: "Tony Robbins", category: "motivation" },
  { text: "The only limit to our realization of tomorrow will be our doubts of today.", author: "Franklin D. Roosevelt", category: "motivation" },
  { text: "Wisdom begins in wonder.", author: "Socrates", category: "wisdom" },
  { text: "The unexamined life is not worth living.", author: "Socrates", category: "wisdom" },
  { text: "Where there is love there is life.", author: "Mahatma Gandhi", category: "love" },
  { text: "Love all, trust a few, do wrong to none.", author: "William Shakespeare", category: "love" },
  { text: "The best way to predict the future is to invent it.", author: "Alan Kay", category: "tech" },
  { text: "Stay hungry, stay foolish.", author: "Steve Jobs", category: "tech" },
  { text: "Everything you've ever wanted is on the other side of fear.", author: "George Addair", category: "motivation" },
  { text: "Quality is not an act, it is a habit.", author: "Aristotle", category: "wisdom" },
  { text: "Simplicity is the ultimate sophistication.", author: "Leonardo da Vinci", category: "wisdom" },
  { text: "Done is better than perfect.", author: "Sheryl Sandberg", category: "tech" }
];

function InspirationFeed({ onSelectQuote }) {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredQuotes = activeCategory === 'all' 
    ? quotes 
    : quotes.filter(q => q.category === activeCategory);

  return (
    <div className="mt-16 border-t border-slate-200 pt-12">
      <div className="text-center mb-10">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Need Inspiration?</h3>
        <p className="text-slate-500">Explore popular quotes across different categories</p>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
              activeCategory === cat.id 
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200' 
                : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
            }`}
          >
            <SafeIcon icon={cat.icon} className="w-4 h-4" />
            <span>{cat.label}</span>
          </button>
        ))}
      </div>

      {/* Quotes Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredQuotes.map((quote, idx) => (
          <motion.div
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            key={quote.text}
            whileHover={{ y: -5 }}
            onClick={() => onSelectQuote(quote.text, quote.author)}
            className="group cursor-pointer bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:border-indigo-300 hover:shadow-xl hover:shadow-indigo-500/10 transition-all"
          >
            <div className="mb-4">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-500 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                <SafeIcon icon={FiZap} className="w-4 h-4" />
              </div>
            </div>
            <p className="text-slate-800 font-medium leading-relaxed mb-4 line-clamp-3 italic">
              "{quote.text}"
            </p>
            <div className="flex items-center justify-between mt-auto">
              <span className="text-xs font-bold text-indigo-600 uppercase tracking-tight">
                — {quote.author}
              </span>
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-slate-50 px-2 py-1 rounded">
                {quote.category}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export default InspirationFeed;